// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — useCommandStack (M3: V·IANet Flow)
// Undo/Redo with Zustand snapshot store (Capa 2A)
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import type { FlowNode, FlowEdge } from '../types/flowchart.types';

const MAX_HISTORY = 50;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FlowSnapshot {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

interface UndoStore {
  past: FlowSnapshot[];
  future: FlowSnapshot[];
  canUndo: boolean;
  canRedo: boolean;
  pushState: (snapshot: FlowSnapshot) => void;
  undo: (current: FlowSnapshot) => FlowSnapshot | null;
  redo: (current: FlowSnapshot) => FlowSnapshot | null;
  clear: () => void;
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useUndoStore = create<UndoStore>((set, get) => ({
  past: [],
  future: [],
  canUndo: false,
  canRedo: false,

  pushState: (snapshot: FlowSnapshot) => {
    set((state) => {
      const newPast = [...state.past, snapshot];
      // Trim to max history
      if (newPast.length > MAX_HISTORY) {
        newPast.shift();
      }
      return {
        past: newPast,
        future: [],
        canUndo: newPast.length > 0,
        canRedo: false,
      };
    });
  },

  undo: (current: FlowSnapshot) => {
    const { past } = get();
    if (past.length === 0) return null;

    const previous = past[past.length - 1];
    const newPast = past.slice(0, -1);

    set((state) => ({
      past: newPast,
      future: [...state.future, current],
      canUndo: newPast.length > 0,
      canRedo: true,
    }));

    return previous;
  },

  redo: (current: FlowSnapshot) => {
    const { future } = get();
    if (future.length === 0) return null;

    const next = future[future.length - 1];
    const newFuture = future.slice(0, -1);

    set((state) => ({
      past: [...state.past, current],
      future: newFuture,
      canUndo: true,
      canRedo: newFuture.length > 0,
    }));

    return next;
  },

  clear: () => {
    set({ past: [], future: [], canUndo: false, canRedo: false });
  },
}));

// ─── Convenience Hook (backwards compatible) ─────────────────────────────────

export interface UseCommandStackReturn {
  pushState: (snapshot: FlowSnapshot) => void;
  undo: (current: FlowSnapshot) => FlowSnapshot | null;
  redo: (current: FlowSnapshot) => FlowSnapshot | null;
  canUndo: boolean;
  canRedo: boolean;
  clear: () => void;
}

export function useCommandStack(): UseCommandStackReturn {
  const { pushState, undo, redo, canUndo, canRedo, clear } = useUndoStore();
  return { pushState, undo, redo, canUndo, canRedo, clear };
}
